import type { BlueprintCalculator } from "@project/emerge-types";
import { Sides } from "@project/emerge-types";
import each from "lodash/each";
import filter from "lodash/filter";
import find from "lodash/find";
import flatten from "lodash/flatten";
import map from "lodash/map";
import sum from "lodash/sum";
import { Box3, Vector3 } from "three";
import {
  calculateGroupAnimations,
  updateGroupAnimationRotationPoints,
} from "../animations/GroupAnimationCalculator";
import { Covers } from "../enum/Covers";
import { InteriorType } from "../enum/InteriorType";
import { LTProducts } from "../enum/LTProducts";
import { ROLLER_TYPE } from "../enum/ROLLER_TYPE";
import { VariableOptionType } from "../enum/VariableOptionType";
import {
  buildTree,
  calculateCaseInteriorFromHeightsWidths,
  calculateTree,
} from "../genetic_optimize/ContentTree";
import type { ConnectorInfos } from "../interfaces/ConnectorInfos";
import type { ConnectorSetInfos } from "../interfaces/ConnectorSetInfos";
import type { FlightCaseBlueprint } from "../interfaces/FlightCaseBlueprint";
import type { FlightCaseSettings } from "../interfaces/FlightCaseSettings";
import type { VariableOption } from "../interfaces/VariableOption";
import { compileConnectorSet } from "../structure/ConnectorSetInfos";
import { CornerInfos } from "../structure/CornerInfos";
import type { EdgeInfos } from "../structure/EdgeInfos";
import type { EndpointInfos } from "../structure/EndpointInfos";
import { DefaultGroupName, Rect } from "../structure/Rect";
import { RectPropertiesBySide } from "../structure/RectProperties";
import {
  getVector3Index,
  resolveAccessoryPlacement,
} from "./interior-helpers/InteriorHelpers";
export const rackUnitHeight = 45;

export const mmToHE = (value: number) =>
  Math.floor((value - 5) / rackUnitHeight);
export const HETomm = (value: number) => value * rackUnitHeight + 5;

export class FlightCaseBlueprintCalculator
  implements BlueprintCalculator<FlightCaseSettings, FlightCaseBlueprint>
{
  public execute(settings: FlightCaseSettings): FlightCaseBlueprint {
    const { innerDimensions, outerDimensions } =
      this.calculateDimensions(settings);

    // Calculate rects for each side
    const rects = this.calculateRects(settings, innerDimensions);

    // Calculate connectors between panels
    const connectors = this.compileConnectors(settings);

    // Calculate corners where three panels meet
    const corners = this.calculateCorners(settings, outerDimensions);

    // Calculate edges where two panels meet
    const edges: EdgeInfos[] = [];

    const blueprint: FlightCaseBlueprint = {
      type: settings.type,
      innerDimensions,
      outerDimensions,
      rects,
      rectsById: new Map(rects.map((rect) => [rect.id, rect])),
      connectors,
      corners,
      edges,
      groupAnimations: [], // Wird später in calculateGroupAnimationsWithRotationPoints gesetzt
    };

    this.updateIndices(blueprint);
    this.distributeConnectors(blueprint);

    return blueprint;
  }

  public update(
    settings: FlightCaseSettings,
    _previousBlueprint: FlightCaseBlueprint,
    _changes: FlightCaseSettings,
  ): FlightCaseBlueprint {
    // For now, just recalculate everything
    // TODO: Implement incremental updates based on what changed
    return this.execute(settings);
  }

  private calculateOuterDimensions(
    innerDimensions: Vector3,
    strength: number,
  ): Vector3 {
    return innerDimensions
      .clone()
      .add(new Vector3(2 * strength, 2 * strength, 2 * strength));
  }

  private calculateCaseDepth(
    baseDepth: number,
    cuts: FlightCaseSettings["cuts"],
    profiles: FlightCaseSettings["exteriorOptions"]["profiles"],
  ): number {
    let caseDepth = baseDepth;
    for (const side of [Sides.BACK, Sides.FRONT]) {
      const matchingCut = cuts.find((c) => c.reference === side);
      if (matchingCut) {
        caseDepth += profiles.height;
        caseDepth += matchingCut.offset;
        caseDepth += profiles.extra;
      } else {
        caseDepth += 5;
      }
    }
    return caseDepth;
  }

  private calculateRackRailOffsetHeight(
    rackRailOffsets?: { [cutName: string]: number },
    rackRailDisabled?: { [cutName: string]: boolean },
  ): number {
    if (
      rackRailOffsets?.[Covers.TOP_COVER] &&
      rackRailOffsets[Covers.TOP_COVER] > 0 &&
      !rackRailDisabled?.[Covers.TOP_COVER]
    ) {
      return rackRailOffsets[Covers.TOP_COVER];
    }
    return 0;
  }

  private calculateRackRailOffsetDepth(
    rackRailOffsets?: { [cutName: string]: number },
    rackRailDisabled?: { [cutName: string]: boolean },
  ): number {
    let offset = 0;
    if (
      rackRailOffsets?.[Covers.FRONT_COVER] &&
      rackRailOffsets[Covers.FRONT_COVER] > 0 &&
      !rackRailDisabled?.[Covers.FRONT_COVER]
    ) {
      offset += rackRailOffsets[Covers.FRONT_COVER];
    }
    if (
      rackRailOffsets?.[Covers.BACK_COVER] &&
      rackRailOffsets[Covers.BACK_COVER] > 0 &&
      !rackRailDisabled?.[Covers.BACK_COVER]
    ) {
      offset += rackRailOffsets[Covers.BACK_COVER];
    }
    return offset;
  }

  private calculateCaseVolumes(
    settings: FlightCaseSettings,
  ): Array<{ sizeWithPadding: Vector3; center: Vector3 }> {
    if (settings.interiorOptions.type !== InteriorType.CASE) {
      return [];
    }
    const caseOptions = settings.interiorOptions;
    const { tree, contents, heights, widths, depth } = caseOptions;
    const panelStrength = caseOptions.trayMaterial.strength;
    const tolerance = caseOptions.tolerance;

    if (tree) {
      return calculateTree(tree, panelStrength, tolerance).volumes;
    }
    if (contents && contents.length > 0) {
      const convertedTree = buildTree(contents);
      return calculateTree(convertedTree, panelStrength, tolerance).volumes;
    }
    if (heights && widths && depth) {
      return calculateCaseInteriorFromHeightsWidths(
        heights,
        widths,
        depth,
        panelStrength,
        tolerance,
      ).volumes;
    }
    return [];
  }

  private calculateCaseDimensions(settings: FlightCaseSettings) {
    if (settings.interiorOptions.type !== InteriorType.CASE) {
      throw new Error("Expected CASE interior type");
    }
    const caseOptions = settings.interiorOptions;

    const topCoverCut =
      find(settings.cuts, { reference: Sides.TOP }) ||
      find(settings.cuts, { reference: Sides.BOTTOM });
    const paddingTop =
      topCoverCut && Object.values(settings.coverPaddings).length > 0
        ? (Object.values(settings.coverPaddings)[0]?.areaPaddingType.strength ??
          0)
        : 0;

    const volumes = this.calculateCaseVolumes(settings);
    const contentsBox = new Box3();
    for (const volume of volumes) {
      contentsBox.union(
        new Box3().setFromCenterAndSize(
          volume.center,
          volume.sizeWithPadding.clone(),
        ),
      );
    }

    const innerDimensions = new Vector3();
    contentsBox.getSize(innerDimensions);

    const { accessoryCompartment } = caseOptions;
    if (accessoryCompartment?.side !== undefined) {
      const rectProps = RectPropertiesBySide[accessoryCompartment.side];
      if (rectProps && accessoryCompartment.padding) {
        const { normal } = rectProps;
        // A centred compartment sits between two dividers instead of one, and
        // it replaces the content divider that used to sit on the centre line.
        // Must match what CaseInteriorBuildStep actually builds, or the case
        // ends up a divider too wide.
        const placement = resolveAccessoryPlacement(
          accessoryCompartment,
          volumes,
          getVector3Index(normal),
        );
        const dividerCount = placement === "CENTER" ? 2 : 1;
        innerDimensions[normal] +=
          (accessoryCompartment.size ?? 0) +
          2 * (accessoryCompartment.padding.strength ?? 0) +
          dividerCount * caseOptions.trayMaterial.strength;
        if (placement === "CENTER") {
          innerDimensions[normal] -= caseOptions.trayMaterial.strength;
        }
      }
    }

    innerDimensions.y += paddingTop;
    const outerDimensions = this.calculateOuterDimensions(
      innerDimensions,
      settings.exteriorOptions.strength,
    );

    return { innerDimensions, outerDimensions };
  }

  private calculateRackDimensions(settings: FlightCaseSettings) {
    if (settings.interiorOptions.type !== InteriorType.RACK) {
      throw new Error("Expected RACK interior type");
    }
    const rackOptions = settings.interiorOptions;
    const { depth, heights, trayMaterial, width } = rackOptions;
    const caseDepth = this.calculateCaseDepth(
      depth,
      settings.cuts,
      settings.exteriorOptions.profiles,
    );

    const innerDimensions = new Vector3(
      typeof width === "number" ? width : 0,
      sum(heights) + (heights.length - 1) * trayMaterial.strength,
      caseDepth,
    );

    const outerDimensions = this.calculateOuterDimensions(
      innerDimensions,
      settings.exteriorOptions.strength,
    );

    return { innerDimensions, outerDimensions };
  }

  private calculateRack19Dimensions(settings: FlightCaseSettings) {
    if (settings.interiorOptions.type !== InteriorType.RACK19) {
      throw new Error("Expected RACK19 interior type");
    }
    const rack19Options = settings.interiorOptions;

    const topCut = find(settings.cuts, { reference: Sides.TOP });
    const cutHeight = topCut ? topCut.offset : 0;
    const { extra, height } = settings.exteriorOptions.profiles;

    const caseDepth = this.calculateCaseDepth(
      rack19Options.depth,
      settings.cuts,
      settings.exteriorOptions.profiles,
    );

    const rackRailOffsetHeight = this.calculateRackRailOffsetHeight(
      rack19Options.rackRailOffsets,
      rack19Options.rackRailDisabled,
    );

    const rackRailOffsetDepth = this.calculateRackRailOffsetDepth(
      rack19Options.rackRailOffsets,
      rack19Options.rackRailDisabled,
    );

    const rackRails = rack19Options.rackRails;
    const underlayStrength = rack19Options.underlayStrength ?? 0;
    const rackRailsSpacing = rackRails?.spacing ?? 0;
    const width =
      rack19Options.width + 2 * rackRailsSpacing + 2 * underlayStrength;

    const heightOffset = [LTProducts.Case3d, LTProducts.LCase].includes(
      settings.type,
    )
      ? cutHeight + extra + height
      : 0;

    const innerDimensions = new Vector3(
      width,
      rack19Options.height + rackRailOffsetHeight + heightOffset,
      caseDepth + rackRailOffsetDepth,
    );

    const outerDimensions = this.calculateOuterDimensions(
      innerDimensions,
      settings.exteriorOptions.strength,
    );

    return { innerDimensions, outerDimensions };
  }

  private calculateDimensions(settings: FlightCaseSettings) {
    switch (settings.interiorOptions.type) {
      case InteriorType.CASE:
        return this.calculateCaseDimensions(settings);
      case InteriorType.RACK:
        return this.calculateRackDimensions(settings);
      case InteriorType.RACK19:
        return this.calculateRack19Dimensions(settings);
    }
  }
  private updateIndices(blueprint: FlightCaseBlueprint) {
    // create an index of uniqe edges
    blueprint.edges = [];
    for (const rect of blueprint.rects) {
      blueprint.rectsById.set(rect.id, rect);
      for (const edge of rect.edges) {
        const sameEdge = find(blueprint.edges, (e) => e.equals(edge));
        if (sameEdge) {
          sameEdge.rects.push(rect);
          // keep indices synced
          edge.rects = sameEdge.rects;
        } else {
          edge.rects.push(rect);
          blueprint.edges.push(edge);
        }
      }
    }
    // interlink corners, edges and rects with indices for better lookup later on
    blueprint.corners.forEach((corner: CornerInfos) => {
      const matchingRect: Rect = find(blueprint.rects, (r: Rect) =>
        find(r.corners, (endPoint: EndpointInfos) =>
          endPoint.outerPos.equals(corner.position),
        ),
      ) as Rect;
      if (matchingRect) {
        corner.group = matchingRect.group;
      }
      each(blueprint.edges, (edge) => {
        if (edge.start.outerPos.equals(corner.position)) {
          edge.start.corner = corner;
          corner.edges.push(edge);
        }
        if (edge.end.outerPos.equals(corner.position)) {
          edge.end.corner = corner;
          corner.edges.push(edge);
        }
      });
    });
  }
  private compileConnectors(settings: FlightCaseSettings) {
    const result = flatten(
      map(
        filter(settings.variableOptions, "connectorType"),
        (connectorOption: VariableOption): ConnectorInfos[] => {
          switch (connectorOption.variableOptionType) {
            case VariableOptionType.HINGE_SET:
            case VariableOptionType.LOCK_SET: {
              return compileConnectorSet(connectorOption as ConnectorSetInfos);
            }
            default:
              throw new Error("unknown connector type");
          }
        },
      ),
    );
    return result;
  }
  private matchConnectorsToEdge(
    edge: EdgeInfos,
    connectors: FlightCaseBlueprint["connectors"],
  ): void {
    if (edge.cutName === null) return;
    edge.connectors = [];
    for (const con of connectors) {
      if (edge.cutName === con.group && edge.sides[0] === +con.side) {
        edge.addConnector(con);
      }
    }
  }

  private distributeConnectors(blueprint: FlightCaseBlueprint) {
    for (const rect of blueprint.rects) {
      for (const edge of rect.edges) {
        this.matchConnectorsToEdge(edge, blueprint.connectors);
      }
    }
  }
  private calculateRects(
    settings: FlightCaseSettings,
    innerDimensions: Vector3,
  ): Rect[] {
    // create a cube out of 6 rects
    let result: Rect[] = [];
    for (const side of [
      Sides.LEFT,
      Sides.RIGHT,
      Sides.FRONT,
      Sides.BOTTOM,
      Sides.TOP,
      Sides.BACK,
    ]) {
      // Ensure strength is always a number (convert from string if needed)
      const strength = Number(settings.exteriorOptions.strength);
      if (Number.isNaN(strength)) {
        throw new Error(
          `Invalid strength value: ${settings.exteriorOptions.strength}`,
        );
      }
      const rect = new Rect(side, innerDimensions, strength);
      result.push(rect);
    }

    // apply the cuts
    for (const cut of settings.cuts) {
      const newRects: Rect[] = [];

      for (const rect of result) {
        if (!rect) continue; // Sicherheitscheck

        const newRect = rect.applyCut(
          cut,
          settings.exteriorOptions.profiles.extra,
        );

        if (newRect) {
          newRects.push(newRect);
        }
      }

      result = result.concat(newRects);
    }

    return result;
  }
  private calculateCornerPosition(
    sideX: Sides,
    sideY: Sides,
    sideZ: Sides,
    outerDimensions: Vector3,
  ): Vector3 {
    return new Vector3(
      sideX === Sides.RIGHT ? 0.5 : -0.5,
      sideY === Sides.TOP ? 0.5 : -0.5,
      sideZ === Sides.FRONT ? 0.5 : -0.5,
    ).multiply(outerDimensions);
  }

  private shouldMirrorCorner(corner: CornerInfos, rollerSide: Sides): boolean {
    const hasBack = corner.sides.includes(Sides.BACK);
    const hasRight = corner.sides.includes(Sides.RIGHT);
    const hasFront = corner.sides.includes(Sides.FRONT);
    const hasLeft = corner.sides.includes(Sides.LEFT);
    // FRONT/BACK pair and LEFT/RIGHT pair use independent rules; no fall-through.
    // Without this split, a FRONT/BACK pair ends up with both corners mirrored
    // (regression from the pre-refactor if/else), flipping one roller's handedness.
    if (rollerSide === Sides.BACK || rollerSide === Sides.FRONT) {
      return hasBack === hasRight;
    }
    return hasFront !== hasLeft;
  }

  private applyRollerToCorner(
    corner: CornerInfos,
    rollers: FlightCaseSettings["exteriorOptions"]["rollers"],
  ): void {
    const isCornerRoller =
      rollers.rollerType === ROLLER_TYPE.CORNER_INBOARD ||
      rollers.rollerType === ROLLER_TYPE.CORNER_OUTBOARD;

    if (!isCornerRoller) return;

    const hasBottomSide = corner.sides.includes(Sides.BOTTOM);
    const hasRollerSide = corner.sides.includes(rollers.side);

    if (!hasBottomSide || !hasRollerSide) return;

    corner.partSelection = rollers;
    if (this.shouldMirrorCorner(corner, rollers.side)) {
      corner.mirror = true;
    }
  }

  private createCorner(
    sideX: Sides,
    sideY: Sides,
    sideZ: Sides,
    position: Vector3,
    settings: FlightCaseSettings,
  ): CornerInfos {
    return new CornerInfos(
      sideX,
      sideY,
      sideZ,
      position,
      DefaultGroupName,
      sideY === Sides.TOP
        ? settings.exteriorOptions.cornersTop
        : settings.exteriorOptions.cornersBottom,
    );
  }

  private calculateCorners(
    settings: FlightCaseSettings,
    outerDimensions: Vector3,
  ): CornerInfos[] {
    const corners: CornerInfos[] = [];
    for (const sideX of [Sides.LEFT, Sides.RIGHT]) {
      for (const sideZ of [Sides.FRONT, Sides.BACK]) {
        for (const sideY of [Sides.TOP, Sides.BOTTOM]) {
          const position = this.calculateCornerPosition(
            sideX,
            sideY,
            sideZ,
            outerDimensions,
          );

          const corner = this.createCorner(
            sideX,
            sideY,
            sideZ,
            position,
            settings,
          );

          this.applyRollerToCorner(corner, settings.exteriorOptions.rollers);
          corners.push(corner);
        }
      }
    }
    return corners;
  }

  /**
   * Berechnet die vollständigen Gruppen-Animationen inklusive Rotationspunkte
   * Diese Methode sollte nach den BuildSteps aufgerufen werden
   */
  public calculateGroupAnimationsWithRotationPoints(
    blueprint: FlightCaseBlueprint,
    hingeDefinitions: Array<{
      group?: string;
      position: Vector3;
      partSelection?: { hingeOffset?: number };
      side?: Sides;
    }>,
    settings: FlightCaseSettings,
  ): void {
    // Erst die grundlegenden Animationen aus den Connectors berechnen
    const basicAnimations = calculateGroupAnimations(
      blueprint.connectors,
      settings.cuts, // Übergebe Cut-Informationen aus Settings
      blueprint.innerDimensions, // Übergebe innerDimensions für Distanz-Berechnung
    );

    // Dann die Rotationspunkte für Scharniere hinzufügen
    blueprint.groupAnimations = updateGroupAnimationRotationPoints(
      basicAnimations,
      hingeDefinitions,
    );
  }
}
